import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Loader2, ArrowRight, Save, RefreshCw, Copy, CheckCircle2, X, MessageSquare } from 'lucide-react';

export const renderStep6 = ({
  step6Data,
  scenePrompts,
  currentSceneId,
  loading,
  handleSaveAndExit,
  regenerateStep,
  generateScenePrompt,
  setCurrentSceneId,
  goToFinalStep,
  step3Data
}) => {
  if (!step6Data) return null;

  const { scenes_summary, total_scenes, scenes_completed, next_scene_id } = step6Data;
  const allScenesCompleted = scenes_completed.length === total_scenes;

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Paso 6: Prompts por Escena (Ultra-Detallados)</CardTitle>
            <CardDescription>
              Desarrolla cada escena con prompts profesionales de 500+ palabras
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
              <Save className="w-4 h-4 mr-2" />
              Guardar y Salir
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Progress Indicator */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-purple-900">
                Progreso: {scenes_completed.length}/{total_scenes} escenas completadas
              </h4>
              <span className="text-sm text-purple-700">
                {Math.round((scenes_completed.length / total_scenes) * 100)}%
              </span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(scenes_completed.length / total_scenes) * 100}%` }}
              />
            </div>
          </div>

          {/* Scene List */}
          <div className="grid gap-3">
            {scenes_summary?.map((scene) => {
              const isCompleted = scenes_completed.includes(scene.id);
              const isCurrent = currentSceneId === scene.id;
              
              return (
                <Card 
                  key={scene.id}
                  className={`transition-all ${
                    isCompleted ? 'border-green-300 bg-green-50' :
                    isCurrent ? 'border-purple-400 bg-purple-50 border-2' :
                    'border-gray-200'
                  }`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-lg">Escena {scene.id}</span>
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {scene.duration}
                          </span>
                          {isCompleted && (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        <p className="text-sm text-gray-700">{scene.action}</p>
                        
                        {/* Show prompt preview if completed */}
                        {isCompleted && scenePrompts[scene.id] && (
                          <div className="mt-3 p-3 bg-white rounded border">
                            <p className="text-xs text-gray-600 mb-1 font-semibold">
                              Frame Prompt Preview:
                            </p>
                            <p className="text-xs font-mono text-gray-700 line-clamp-2">
                              {scenePrompts[scene.id].frame_prompt}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="ml-4">
                        {isCompleted ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setCurrentSceneId(scene.id)}
                          >
                            Ver/Editar
                          </Button>
                        ) : scene.id === next_scene_id ? (
                          <Button 
                            size="sm"
                            onClick={() => generateScenePrompt(scene.id)}
                            disabled={loading}
                          >
                            {loading ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /></>
                            ) : (
                              'Desarrollar'
                            )}
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400 px-3 py-2">Bloqueado</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Next Action */}
          {allScenesCompleted ? (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300">
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle2 className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-blue-900 mb-2">
                    ¡Todas las Escenas Completadas!
                  </h3>
                  <p className="text-blue-800 mb-4">
                    Ahora genera los prompts finales de música y voz
                  </p>
                  <Button 
                    onClick={goToFinalStep}
                    className="h-12"
                    disabled={loading}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Generando...</>
                    ) : (
                      <>
                        Siguiente: Prompts de Música y Voz
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600">
                💡 <strong>Tip:</strong> Cada escena se desarrolla con prompts ultra-detallados de 500+ palabras
                para Nano Banana y 300+ palabras para Runway Gen-3
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const renderScenePromptDetail = ({
  currentSceneId,
  scenePrompts,
  step3Data,
  step6Data,
  loading,
  handleCopy,
  handleSaveAndExit,
  regenerateScenePrompt,
  backToSceneList,
  goToFinalStep,
  showRegenerateModal,
  setShowRegenerateModal,
  regenerateFeedback,
  setRegenerateFeedback
}) => {
  const promptData = scenePrompts[currentSceneId];
  const scene = step3Data?.scenes?.find(s => s.id === currentSceneId);
  
  // Check if all scenes are completed
  const allScenesCompleted = step6Data && step6Data.scenes_completed?.length === step6Data.total_scenes;
  
  if (!promptData || !scene) return null;

  const handleRegenerateClick = () => {
    setShowRegenerateModal(true);
    setRegenerateFeedback('');
  };

  const handleRegenerateConfirm = () => {
    setShowRegenerateModal(false);
    regenerateScenePrompt(currentSceneId, regenerateFeedback);
  };

  const handleRegenerateCancel = () => {
    setShowRegenerateModal(false);
    setRegenerateFeedback('');
  };

  return (
    <>
      {/* Modal de Regeneración con Observaciones */}
      {showRegenerateModal && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={handleRegenerateCancel}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  Regenerar Escena {currentSceneId}
                </h2>
                <button
                  onClick={handleRegenerateCancel}
                  className="text-white/80 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Observaciones para mejorar (opcional)
                </label>
                <Textarea
                  value={regenerateFeedback}
                  onChange={(e) => setRegenerateFeedback(e.target.value)}
                  placeholder="Ej: Quiero que el protagonista esté más cerca de la cámara, con iluminación más dramática y colores más cálidos..."
                  rows={4}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-2">
                  💡 Describe qué te gustaría cambiar o mejorar en esta escena
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleRegenerateCancel}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRegenerateConfirm}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Regenerando...</>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 w-4 h-4" />
                      Regenerar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Card className="max-w-5xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Escena {currentSceneId} - Prompts Detallados</CardTitle>
              <CardDescription>{scene.action}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={backToSceneList}>
                ← Volver a Lista
              </Button>
              <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
              <Button variant="outline" size="sm" onClick={handleRegenerateClick} disabled={loading}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Frame Prompt (Nano Banana) */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-purple-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">🖼️ Frame Prompt (Nano Banana)</CardTitle>
                    <CardDescription>
                      {promptData.frame_prompt.length} caracteres • Ultra-detallado
                    </CardDescription>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleCopy(promptData.frame_prompt)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="bg-white p-4 rounded border max-h-96 overflow-y-auto">
                  <p className="text-sm font-mono text-gray-800 whitespace-pre-wrap">
                    {promptData.frame_prompt}
                  </p>
                </div>
                
                {promptData.frame_negative_prompt && (
                  <div className="bg-red-50 p-3 rounded border border-red-200">
                    <p className="text-xs font-semibold text-red-900 mb-2">❌ Negative Prompt:</p>
                    <p className="text-xs font-mono text-red-800">
                      {promptData.frame_negative_prompt}
                    </p>
                  </div>
                )}

                {promptData.frame_technical_params && (
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="text-xs font-semibold text-blue-900 mb-2">🎥 Technical Parameters:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
                      {Object.entries(promptData.frame_technical_params).map(([key, value]) => (
                        <div key={key}><strong>{key}:</strong> {value}</div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Video Prompt (Runway) */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">🎥 Video Prompt (Runway Gen-3)</CardTitle>
                    <CardDescription>
                      {promptData.video_prompt.length} caracteres • Dirección específica
                    </CardDescription>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleCopy(promptData.video_prompt)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="bg-white p-4 rounded border max-h-96 overflow-y-auto">
                  <p className="text-sm font-mono text-gray-800 whitespace-pre-wrap">
                    {promptData.video_prompt}
                  </p>
                </div>

                {promptData.video_technical_params && (
                  <div className="bg-indigo-50 p-3 rounded">
                    <p className="text-xs font-semibold text-indigo-900 mb-2">⚙️ Video Parameters:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-indigo-800">
                      {Object.entries(promptData.video_technical_params).map(([key, value]) => (
                        <div key={key}><strong>{key}:</strong> {value}</div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Production Notes */}
            {promptData.production_notes && promptData.production_notes.length > 0 && (
              <Card className="border-2 border-yellow-200">
                <CardHeader className="bg-yellow-50">
                  <CardTitle className="text-lg">📝 Production Notes</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-2">
                    {promptData.production_notes.map((note, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex">
                        <span className="mr-2">•</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Button 
              variant="outline"
              onClick={backToSceneList}
              className="w-full"
            >
              ← Volver a Lista de Escenas
            </Button>

            {/* Show button to go to final step if all scenes are completed */}
            {allScenesCompleted && (
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 mt-4">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <CheckCircle2 className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-blue-900 mb-2">
                      ¡Todas las Escenas Completadas!
                    </h3>
                    <p className="text-blue-800 mb-4 text-sm">
                      Continúa al paso final para generar los prompts de música y voz
                    </p>
                    <Button 
                      onClick={goToFinalStep}
                      className="h-12"
                      disabled={loading}
                    >
                      {loading ? (
                        <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Generando...</>
                      ) : (
                        <>
                          Ir a Paso 7: Audio Final
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};
